import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { ThemeOptions } from '@mui/material';
import { makeStyles } from '@mui/styles';
import DialogEVAWidget from '@/components/DialogEVAWidget/DialogEVAWidget';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Button from '@/components/UI/Button/Button';
import Calendar from '@/components/UI/Calendar/Calendar';
import { ContainerGrid, ContainerGridItem } from '@/components/UI/ContainerGrid/ContainerGrid';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import InputText from '@/components/UI/InputText/InputText';
import InputUser from '@/components/UI/InputUser/InputUser';
import LabelDisabledByPDRS from '@/components/UI/LabelDisabledByPDRS/LabelDisabledByPDRS';
import Spinner from '@/components/UI/Spinner/Spinner';
import { getNewState } from '@/helpers/campaignValidations';
import { DESCRIPTION, DOORMAN_PROCESS_KEY, HELP_AREA, NUMERIC_FIELDS, PDRS, SDR, additionalContactPerson, descriptionIssues, isNotPRO, statusCampaign } from '@/helpers/constants';
import { stringToDate, checkDate, buildNewSelectedUser, isFieldDisabledBYPRRD, isMPDepartment } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useDetectLanguage from '@/hooks/useDetectLanguage';
import useError from '@/hooks/useError';
import useToastContext from '@/hooks/useToastContext';
import { setCampaignDescription, setMinActiveValidations, setDescriptionValidations, setOriginalValues } from '@/redux/Campaign/campaign.action';
import { getCampaignChanges, getCampaignInfo, getCampaignDescription, getCampaignSearch, getCampaignValidationsActive, getDescriptionValidations, getOriginalValues, getHighlightTab, getIsVANCR } from '@/redux/Campaign/campaign.reducer';
import { setDescriptionForms } from '@/redux/Forms/forms.action';
import { getDescriptionForms } from '@/redux/Forms/forms.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import campaignServices from '@/services/campaignServices';
import { CampaignInfo, CampaignSearch, Validations } from '@/types';
import { Description as DescriptionType } from '@/types/Description';
import { errorMessage, getMessage, maxLengthError } from './Description.utils';
import InspectionSection from './InspectionSection';
import PossibleCauserSection from './PossibleCauserSection';
import ProblemsSection from './ProblemsSection';
import styles from './Description.module.scss';

const useStyles = makeStyles((theme: ThemeOptions) => ({
  disabled: { backgroundColor: theme.cartTheme?.contentBackgroundColor },
}));

const DESCRIPTION_SECTION = 'description';

const commentsFields = [DESCRIPTION.TRIGGER_COMMENT, DESCRIPTION.PROBLEMS, DESCRIPTION.INSPECTION_COMMENT];
const validateFields = [DESCRIPTION.PROBLEMS, DESCRIPTION.SOURCE, DESCRIPTION.FAILURE_OCCURRED_ON];

interface DescriptionProps {
  subCR: boolean;
  setDescription: (description: DescriptionType) => void;
  description: DescriptionType;
  setDescriptForms: (forms: any) => object;
  descriptionForms: any;
  setOriginValues: (p: { values: DescriptionType | {}; hasChange: boolean }) => void;
  originalValues: { values: DescriptionType | object; hasChange: boolean };
  campaignInfo: CampaignInfo;
  headerLanguage: string;
  descValidations: Validations;
  activeValidations: any;
  setDescValidations: any;
  setActiveMinLenghtValidations: any;
  campaignSearch: CampaignSearch;
  setHelpArea: (help: string) => void;
  highlightTab: string;
  isVANCR: boolean;
}

const Description: React.FC<DescriptionProps> = (props) => {
  const { subCR, setDescription, description, setDescriptForms, descriptionForms, setOriginValues,
    originalValues, campaignInfo, headerLanguage, descValidations, activeValidations, setDescValidations,
    setActiveMinLenghtValidations, isVANCR, setHelpArea, highlightTab } = props;
  const { id, version } = useParams<{ id?: string; version?: string }>();

  const checkDisabled = useCheckDisabled(true) || subCR;
  const [tabSelected, setTabSelected] = useState(descriptionIssues[0]);
  const [sourceOptions, setSourceOptions] = useState({});
  const [isOpenEVAModal, setIsOpenEVAModal] = useState(false);
  const classes = useStyles();
  const { t } = useTranslation();

  const { detectLanguage, detectLanguageTranslation } = useDetectLanguage();
  const { addToast } = useToastContext();
  const { handleCriticalError } = useError();
  const fetchCampaign = useApiRequest(campaignServices.getCampaignRetryWithSignal);
  const fetchSubCampaign = useApiRequest(campaignServices.getSubCampaignWithSignal);
  const fetchForms = useApiRequest(campaignServices.getTabFormsWithSignal);
  const descriptionRef = useRef(description);
  const efaWasModified = useRef(false);
  const fetchCampaignRef = useRef(fetchCampaign);
  const fetchSubCampaignRef = useRef(fetchSubCampaign);
  const fetchFormsRef = useRef(fetchForms);
  const handleCriticalErrorRef = useRef(handleCriticalError);

  fetchCampaignRef.current = fetchCampaign;
  fetchSubCampaignRef.current = fetchSubCampaign;
  fetchFormsRef.current = fetchForms;
  handleCriticalErrorRef.current = handleCriticalError;

  const isLoading = !descriptionForms?.referenceSystem || !description?.id;
  useEffect(() => {
    descriptionRef.current = description;
  }, [description]);

  const isTriggerMandatory = description?.source === 'OTHER';
  const isInspectionMandatory = description?.inspectionType?.length > 0;
  const isTriggerCommentDisabled = checkDisabled || !description?.source || !description?.failureOccurredOn || isFieldDisabledBYPRRD(campaignInfo, 'description.buildUpApproval');
  const isDoormanProcess = description?.source === DOORMAN_PROCESS_KEY;
  const numberIDLabel = isDoormanProcess ? t('involvedParties.doormanRequestID') : t('involvedParties.iraNumber');
  const isSDRConnection = campaignInfo?.creationSystem === PDRS;
  const isDescriptionForms: boolean = Object.keys(descriptionForms || {}).length > 0;
  const currentLangValidations = (typeof descValidations?.[headerLanguage] === 'object' && descValidations?.[headerLanguage] !== null
    ? descValidations[headerLanguage]
    : {}) as Record<string, any>;
  const isCreationStatus = campaignInfo?.status === statusCampaign.IN_CREATION;

  const onOriginValues = (content: undefined | DescriptionType) => { // if change content is empty, if set with content
    setOriginValues({
      values: content || originalValues,
      hasChange: !content,
    });
  };

  const setDescriptionSafe = useCallback((nextDescription) => {
    if (!nextDescription || typeof nextDescription !== 'object') {
      return;
    }

    descriptionRef.current = nextDescription;
    setDescription(nextDescription);
  }, [setDescription]);

  const activeShowMinError = (label, value) => (value?.length >= 8 && commentsFields.includes(label))
    && setActiveMinLenghtValidations(label === DESCRIPTION.PROBLEMS ? tabSelected : label);

  const validateField = (label, value) => {
    activeShowMinError(label, value);
    let newState;

    switch (true) {
      case (label === DESCRIPTION.TRIGGER_COMMENT && isTriggerMandatory):
      case (label === DESCRIPTION.INSPECTION_COMMENT && isInspectionMandatory):
        newState = {
          ...descValidations,
          [headerLanguage]: getNewState(currentLangValidations, label, value, null, activeValidations?.min?.[label]),
        };
        setDescValidations(newState);
        break;
      case validateFields.includes(label):
        newState = {
          ...descValidations,
          [headerLanguage]: getNewState(currentLangValidations, label, value, tabSelected,
            activeValidations?.min?.[label === DESCRIPTION.PROBLEMS ? tabSelected : label]),
        };
        setDescValidations(newState);
        break;
      case label === DESCRIPTION.CONTACT_PERSON_SUPPL_MANAGEMENT:
        newState = {
          ...descValidations,
          [headerLanguage]: {
            ...currentLangValidations,
            locationExternal: getNewState(currentLangValidations?.locationExternal, label, value),
          },
        };
        setDescValidations(newState);
        break;
      case label === DESCRIPTION.POSSIBLE_CAUSER:
        newState = {
          ...descValidations,
          [headerLanguage]: {
            ...currentLangValidations,
            locationInternal: getNewState(currentLangValidations?.locationInternal, label, value),
          },
        };
        setDescValidations(newState);
        break;
      case label === additionalContactPerson:
        newState = {
          ...descValidations,
          [headerLanguage]: {
            ...currentLangValidations,
            locationExternal: getNewState(currentLangValidations?.locationExternal, label, value),
          },
        };
        setDescValidations(newState);
        break;
      case label === DESCRIPTION.COST_COVERAGE_ACKNOWLEDGED:
        newState = {
          ...descValidations,
          [headerLanguage]: {
            ...currentLangValidations,
            locationExternal: getNewState(currentLangValidations?.locationExternal, label, value),
          },
        };
    };
  };

  const handleChange = (label, value, isLang = false) => {
    validateField(label, value);
    const newState = {
      ...description,
      [label]: isLang ? { ...description[label], [headerLanguage]: value } : value,
    };

    if (label === DESCRIPTION.FAILURE_OCCURRED_ON && !value) {
      newState.triggerComment[headerLanguage] = '';
    }
    onOriginValues(undefined);
    setDescriptionSafe(newState);
  };

  const updateState = (newState) => {
    onOriginValues(undefined);
    setDescriptionSafe(newState);
  };

  const updateUserContactPerson = (userSelected) => handleChange(DESCRIPTION.CONTACT_PERSON, buildNewSelectedUser(userSelected));

  useEffect(() => {
    if (!description?.id) return;

    const currentDescription = descriptionRef.current;

    if (['LCS', 'RVP'].includes(description?.referencedSystem)) {
      const newState = {
        ...currentDescription,
        iraNumber: '',
        contactPerson: buildNewSelectedUser(null),
      };
      setDescriptionSafe(newState);
    } else if (description?.referencedSystem !== 'OTHER' && description?.referencedSystemName) {
      const newState = {
        ...currentDescription,
        referencedSystemName: '',
      };
      setDescriptionSafe(newState);
    }
  }, [description?.id, description?.referencedSystem, description?.referencedSystemName, setDescriptionSafe]);

  const loadDescription = useCallback(() => {
    const request = subCR
      ? fetchSubCampaignRef.current(id, DESCRIPTION_SECTION, version).then((fetchedDescription) => fetchedDescription?.module)
      : fetchCampaignRef.current(id, DESCRIPTION_SECTION, version);

    request
      .then((nextDescription) => {
        if (!nextDescription || typeof nextDescription !== 'object') {
          return;
        }

        setOriginValues({
          values: nextDescription,
          hasChange: false,
        });
        setDescriptionSafe(nextDescription);
      })
      .catch(handleCriticalErrorRef.current);
  }, [subCR, id, version, setOriginValues, setDescriptionSafe]);

  useEffect(() => {
    loadDescription();
  }, [loadDescription]);

  useEffect(() => {
    if (!originalValues?.hasChange && efaWasModified.current) {
      efaWasModified.current = false;

      setTimeout(loadDescription, 1800);
    }
  }, [originalValues?.hasChange, loadDescription]);

  useEffect(() => {
    if (!campaignInfo?.creationDate) return;

    fetchFormsRef.current('CAMPAIGN_DESCRIPTION', campaignInfo.creationDate)
      .then((descriptForms) => {
        setDescriptForms(descriptForms?.form);
      })
      .catch(handleCriticalErrorRef.current);
  }, [campaignInfo?.creationDate, setDescriptForms]);

  useEffect(() => {
    if (!isDescriptionForms || description?.id === '') return;

    const optionsCopy = { ...descriptionForms?.source?.options };

    if (!isSDRConnection && isNotPRO) {
      if (description?.source !== SDR && !isVANCR) delete optionsCopy.SDR;
      delete optionsCopy.SDR_ACTION;
    } else if (isSDRConnection && !isVANCR && isNotPRO) {
      delete optionsCopy.SDR;
    }

    setSourceOptions(optionsCopy);
  }, [
    isDescriptionForms,
    description?.id,
    description?.source,
    descriptionForms?.source?.options,
    isSDRConnection,
    isVANCR,
  ]);

  useEffect(() => {
    setTabSelected(highlightTab || descriptionIssues[0]);
  }, [highlightTab]);

  const onBlur = (name, value) => {
    detectLanguage(value);

    if (originalValues?.hasChange) {
      detectLanguageTranslation(name);
    }
  };

  const handleSetEvaFields = ({
    name,
    value,
    values,
  }: {
    name?: string;
    value?: any;
    values?: Record<string, any>;
  }) => {
    const updates = values || (name ? { [name]: value } : {});

    if (!Object.keys(updates).length) {
      return;
    }

    const newState = {
      ...descriptionRef.current,
      efa: {
        ...(descriptionRef.current?.efa || {}),
        ...updates,
      },
    };

    onOriginValues(undefined);
    setDescriptionSafe(newState);
    efaWasModified.current = true;
  };

  const clearEvaField = () => {
    const newState = {
      ...descriptionRef.current,
      efa: {
        ...(descriptionRef.current?.efa || {}),
        selectedModelSeries: '',
        faultLocation: '',
        faultType: '',
      },
    };

    onOriginValues(undefined);
    setDescriptionSafe(newState);
  };

  const openEVAModal = () => {
    const newState = {
      ...descriptionRef.current,
      efa: {
        ...(descriptionRef.current?.efa || {}),
        selectedModelSeries: '',
        faultLocation: '',
        faultType: '',
      },
    };

    onOriginValues(undefined);
    setDescriptionSafe(newState);

    setIsOpenEVAModal(!isOpenEVAModal);
  };

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <AccordionPanel
            title={t('description.trigger')}
            handleClick={() => setHelpArea(HELP_AREA.TRIGGER)}
          >
            <ContainerGrid columns={{ xs: 2, sm: 4, md: 6, lg: 6 }} gap={16}>
              <ContainerGridItem span={{ xs: 2, sm: 2, md: 2, lg: 2 }}>
                <Dropdown
                  id="source"
                  disabled={checkDisabled || isFieldDisabledBYPRRD(campaignInfo, 'description.source')}
                  fieldPath={[DESCRIPTION_SECTION, DESCRIPTION.SOURCE]}
                  isMandatory
                  label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="description.source" fieldName="description.source" />}
                  options={sourceOptions}
                  value={description?.source}
                  handleChange={(ev) => handleChange(DESCRIPTION.SOURCE, ev.target.value)}
                  formControlClass={`w-100 ${checkDisabled ? classes.disabled : ''}`}
                  helperText={getMessage(currentLangValidations?.source, null, t)}
                  language={headerLanguage}
                />
              </ContainerGridItem>
              <ContainerGridItem span={{ xs: 2, sm: 2, md: 2, lg: 1 }}>
                <Calendar
                  id="date-picker-description-failure-occurred-on"
                  disabled={checkDisabled || isFieldDisabledBYPRRD(campaignInfo, 'description.failureOccurredOn')}
                  fieldPath={[DESCRIPTION_SECTION, DESCRIPTION.FAILURE_OCCURRED_ON]}
                  isMandatory
                  label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="description.failureOccurredOn" fieldName="description.failureOccurredOn" />}
                  handleChange={(date) => checkDate({
                    date,
                    label: DESCRIPTION.FAILURE_OCCURRED_ON,
                    handleChange: (label, value) => handleChange(label, value),
                  })}
                  value={stringToDate(description.failureOccurredOn)}
                  pickerClass={checkDisabled ? classes.disabled : ''}
                  margin="none"
                  formControlClass="m-all-0 w-100"
                  helperText={getMessage(currentLangValidations?.failureOccurredOn, null, t)}
                />
              </ContainerGridItem>
              {isSDRConnection && isNotPRO && !isVANCR && (
                <ContainerGridItem span={{ xs: 2, sm: 2, md: 2, lg: 3 }}>
                  <InputText
                    id="build-up-approval"
                    disabled
                    fieldPath={[DESCRIPTION_SECTION, DESCRIPTION.BUILD_UP_APPROVAL]}
                    multiline
                    rows={4}
                    maxLength={256}
                    formControlClass="w-100"
                    label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="description.buildUpApproval" fieldName="description.buildUpApproval" />}
                    handleChange={(ev) => handleChange(DESCRIPTION.BUILD_UP_APPROVAL, ev.target.value, true)}
                    value={(description.buildUpApproval && description.buildUpApproval[headerLanguage]) || ''}
                    onBlur={(ev) => onBlur(DESCRIPTION.BUILD_UP_APPROVAL, ev.target.value)}
                  />
                </ContainerGridItem>
              )}
              <ContainerGridItem span={{ xs: 2, sm: 4, md: 2, lg: 3 }}>
                <div onClick={() => ((isTriggerCommentDisabled && !checkDisabled) ? addToast(t('validations.descriptionSourceRequired'), 'Warning') : null)}>
                  <InputText
                    id="trigger-comment"
                    disabled={isTriggerCommentDisabled}
                    fieldPath={[DESCRIPTION_SECTION, DESCRIPTION.TRIGGER_COMMENT]}
                    otherConditionInvalid={{ and: true, condition: isTriggerMandatory }}
                    isMandatory={isTriggerMandatory}
                    multiline
                    rows={4}
                    maxLength={256}
                    formControlClass="w-100"
                    label={<LabelDisabledByPDRS campaignInfo={campaignInfo} label="description.triggerComment" fieldName="description.triggerComment" />}
                    handleChange={(ev) => handleChange(DESCRIPTION.TRIGGER_COMMENT, ev.target.value, true)}
                    value={description.triggerComment[headerLanguage] || ''}
                    onBlur={(ev) => onBlur(DESCRIPTION.TRIGGER_COMMENT, ev.target.value)}
                    helperText={[
                      ...((!checkDisabled && errorMessage(currentLangValidations?.triggerComment, 256, t))
                        ? [errorMessage(currentLangValidations?.triggerComment, 256, t)]
                        : []),
                      ...(!checkDisabled && maxLengthError(description.triggerComment[headerLanguage], 256, t)
                        ? [maxLengthError(description.triggerComment[headerLanguage], 256, t)]
                        : []),
                    ]}
                  />
                </div>
              </ContainerGridItem>
            </ContainerGrid>
          </AccordionPanel>
          <AccordionPanel
            title={t('description.EVASection')}
          >
            <ContainerGrid columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} gap={16}>
              <Button
                id="EVA_SSL-button"
                handleClick={() => openEVAModal()}
                buttonStyle="PrimaryRound"
                extraButtonClasses={styles.CenterButtonText}
                btnText={t('description.openEvaSsl')}
                disabled={!isCreationStatus}
              />
              <ContainerGridItem span={{ xs: 1 }}>
                <InputText
                  fieldPath={[DESCRIPTION_SECTION, DESCRIPTION.FAULT_LOCATION]}
                  id="input-fault-location"
                  formControlClass="w-100"
                  className={styles.EvaReadOnlyWithClear}
                  disabled={false}
                  label={t('description.faultLocation')}
                  handleChange={() => null}
                  value={description.efa?.faultLocation}
                  clear={description.efa?.faultLocation ? () => clearEvaField() : undefined}
                  autoComplete="off"
                />
              </ContainerGridItem>
              <ContainerGridItem span={{ xs: 1 }}>
                <InputText
                  fieldPath={[DESCRIPTION_SECTION, DESCRIPTION.FAULT_TYPE]}
                  id="input-fault-type"
                  formControlClass="w-100"
                  className={styles.EvaReadOnlyWithClear}
                  disabled={false}
                  label={t('description.faultType')}
                  handleChange={() => null}
                  value={description.efa?.faultType}
                  clear={description.efa?.faultType ? () => clearEvaField() : undefined}
                  autoComplete="off"
                />
              </ContainerGridItem>
            </ContainerGrid>
          </AccordionPanel>
          <AccordionPanel
            title={t('involvedParties.issueResolutionProcess')}
            handleClick={() => setHelpArea(HELP_AREA.ISSUE_RESOLUTION_PROCESS)}
          >
            <ContainerGrid columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} gap={16}>
              <ContainerGridItem span={{ xs: 1 }}>
                <Dropdown
                  isMandatory
                  fieldPath={[DESCRIPTION_SECTION, DESCRIPTION.REF_SYSTEM]}
                  disabled={checkDisabled}
                  formControlClass={`w-100 ${checkDisabled ? classes.disabled : ''}`}
                  id="reference-system"
                  label={t('involvedParties.referenceSystem')}
                  options={descriptionForms.referenceSystem.options}
                  value={description.referencedSystem}
                  handleChange={(ev) => handleChange('referencedSystem', ev.target.value)}
                  language={headerLanguage}
                  helperText={getMessage(currentLangValidations?.referenceSystem, null, t)}
                />
              </ContainerGridItem>
              <ContainerGridItem span={{ xs: 1 }}>
                <InputText
                  fieldPath={[DESCRIPTION_SECTION, DESCRIPTION.IRA_NUMBER]}
                  isMandatory={['SQMS', 'ZEUS', 'CAT', 'OTHER', 'SAP', 'SBM', 'STARC_GENERATED'].includes(description.referencedSystem)
                    || !!(isDoormanProcess && description.referencedSystem)}
                  disabled={checkDisabled || !description.referencedSystem || ((description.referencedSystem === 'LCS'
                    || description.referencedSystem === 'RVP') && !isDoormanProcess)}
                  id={NUMERIC_FIELDS.INPUT_NO}
                  formControlClass="w-100"
                  label={numberIDLabel}
                  handleChange={(ev) => handleChange(DESCRIPTION.IRA_NUMBER, ev.target.value)}
                  value={description.iraNumber}
                  autoComplete="off"
                  helperText={!checkDisabled && getMessage(currentLangValidations?.iraNumber, null, t)
                    ? [getMessage(currentLangValidations?.iraNumber, null, t)]
                    : []}
                />
              </ContainerGridItem>
              <ContainerGridItem span={{ xs: 1 }}>
                <InputUser
                  fieldPath={[DESCRIPTION_SECTION, DESCRIPTION.CONTACT_PERSON]}
                  userFieldPath={[DESCRIPTION_SECTION, DESCRIPTION.CONTACT_PERSON]}
                  formControlClass="w-100"
                  otherConditionInvalid={{
                    and: true,
                    condition: (!isDoormanProcess && description.referencedSystem !== 'LCS'
                      && description.referencedSystem !== 'RVP'),
                  }}
                  isMandatory={['SQMS', 'ZEUS', 'CAT', 'OTHER'].includes(description.referencedSystem) && !isDoormanProcess}
                  disabled={checkDisabled || isDoormanProcess || (['LCS', 'RVP', 'SBM'].includes(description.referencedSystem))}
                  id="contact-person"
                  label={t('involvedParties.contactPersonIRA')}
                  updateUserSelected={(userSelected) => updateUserContactPerson(userSelected)}
                  value={description.contactPerson?.summary}
                  code={description.contactPerson?.code}
                  title={t('involvedParties.contactPersonIRA')}
                  clear={() => { updateUserContactPerson(null); }}
                  helperText={!checkDisabled && getMessage(currentLangValidations?.contactPerson, null, t)
                    ? [getMessage(currentLangValidations?.contactPerson, null, t)]
                    : []}
                />
              </ContainerGridItem>
              {description.referencedSystem === 'OTHER' && (
                <ContainerGridItem span={{ xs: 1 }}>
                  <InputText
                    fieldPath={[DESCRIPTION_SECTION, DESCRIPTION.REF_SYSTEM_NAME]}
                    isMandatory
                    formControlClass="w-100"
                    disabled={checkDisabled}
                    id="referenced-system-name"
                    label={t('involvedParties.referencedSystemName')}
                    handleChange={(ev) => handleChange(DESCRIPTION.REF_SYSTEM_NAME, ev.target.value)}
                    value={description.referencedSystemName}
                    helperText={!checkDisabled && getMessage(currentLangValidations?.referencedSystemName, null, t)
                      ? [getMessage(currentLangValidations?.referencedSystemName, null, t)]
                      : []}
                  />
                </ContainerGridItem>
              )}
            </ContainerGrid>
          </AccordionPanel>
          <ProblemsSection
            checkDisabled={checkDisabled}
            updateState={updateState}
            tabSelected={tabSelected}
            setTabSelected={setTabSelected}
            activeShowMinError={activeShowMinError}
            validateField={validateField}
            onBlur={onBlur}
          />

          <InspectionSection checkDisabled={checkDisabled} updateState={updateState} onBlur={onBlur} validateField={validateField} />

          <PossibleCauserSection checkDisabled={checkDisabled} updateState={updateState} validateField={validateField} isNotMP={!isMPDepartment(campaignInfo?.creationUser?.department)} />
          {isOpenEVAModal && (
            <DialogEVAWidget
              close={() => setIsOpenEVAModal(false)}
              setEvaFields={handleSetEvaFields}
            />
          )}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  description: getCampaignDescription(state),
  descriptionForms: getDescriptionForms(state),
  originalValues: getOriginalValues(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  campaignInfo: getCampaignInfo(state),
  descValidations: getDescriptionValidations(state),
  activeValidations: getCampaignValidationsActive(state),
  campaignChanges: getCampaignChanges(state),
  campaignSearch: getCampaignSearch(state),
  highlightTab: getHighlightTab(state),
  isVANCR: getIsVANCR(state),
});

const actionCreators = {
  setDescription: setCampaignDescription,
  setDescriptForms: setDescriptionForms,
  setOriginValues: setOriginalValues,
  setDescValidations: setDescriptionValidations,
  setActiveMinLenghtValidations: setMinActiveValidations,
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(Description);
